import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch, readApiJson } from '../lib/api';

/**
 * CartContext — panier de préparation des devis (support.meteris.fr).
 *
 * Le panier est persisté côté backend (col_catalog_carts) par utilisateur. On
 * charge au montage, puis chaque mutation synchronise le serveur et renvoie le
 * panier détaillé (totaux + prix calculés).
 *
 * Trois règles, posées ici une fois pour toutes :
 *
 * 1. CIBLAGE EXPLICITE — toute mutation porte le `cart_id` du panier AFFICHÉ
 *    (corps JSON pour POST/PUT, paramètre d'URL pour GET/DELETE, cf.
 *    `catalog_cart_crud.py`). Auparavant chaque mutation écrivait dans « le
 *    panier courant de l'acteur » : changer de brouillon déplaçait la cible de
 *    toutes les modifications suivantes, y compris celles d'un autre onglet.
 *    Le sélecteur multi-panier n'était donc qu'un décor.
 *
 * 2. AUCUNE RÉPONSE N'EST CRUE SUR PAROLE — `setCart(res)` était fait sans
 *    vérifier que `res` était un panier. Un refus du serveur
 *    (`{ok:false, error:…}`) ou un panier introuvable (`{}`) devenait l'état du
 *    panier : l'écran se vidait sous les yeux de l'utilisateur, sans un mot.
 *    `verifierPanier` refuse ces réponses, `erreurPanier` porte le message.
 *
 * 3. ORDRE DES RÉPONSES — chaque opération prend un jeton ; une réponse arrivée
 *    en retard n'écrase jamais un panier ouvert depuis. Sans ce garde-fou, le
 *    chargement initial (panier courant) pouvait recouvrir le panier désigné
 *    par `?cart_id=` selon le hasard du réseau.
 *
 * Expose : cart, cartId, panierModifiable, itemsCount, erreurPanier, et les
 * mutations (addItem, updateItem, …), l'ouverture ciblée (ouvrirPanier) et la
 * bascule d'après envoi (basculerApresEnvoi).
 */
const CartContext = createContext(null);

const ENTETES_JSON = { 'Content-Type': 'application/json' };

// Un panier du schéma Phase 7 (configurateur PC) s'adresse par un ObjectId
// Mongo — 24 caractères hexadécimaux ; le schéma historique utilise un UUID —
// 36 caractères avec tirets. Les deux espaces sont disjoints : on sait donc
// toujours lequel on tient, sans avoir à le demander au serveur.
const MOTIF_OBJECT_ID = /^[0-9a-f]{24}$/i;

export function estIdentifiantPhase7(valeur) {
  return MOTIF_OBJECT_ID.test(String(valeur || '').trim());
}

/**
 * Vérifie qu'une réponse d'API est bien un panier exploitable.
 *
 * Un panier valide porte toujours un `id` (cf. `get_cart_with_details`). Un
 * refus porte `ok:false` + `error`, un panier introuvable renvoie `{}` : les
 * deux doivent devenir une erreur lisible, jamais l'état du panier.
 */
function verifierPanier(res, contexte) {
  if (res && typeof res === 'object' && res.ok === false) {
    throw new Error(res.error || `Échec : ${contexte}.`);
  }
  if (!res || typeof res !== 'object' || !res.id) {
    throw new Error(`${contexte} : le serveur n'a pas renvoyé de panier exploitable.`);
  }
  return res;
}

export function CartProvider({ children }) {
  const [cart, setCart] = useState(null);
  const [carts, setCarts] = useState([]);  // Liste des drafts pour multi-paniers
  const [loading, setLoading] = useState(false);
  const [erreurPanier, setErreurPanier] = useState(null);

  // Identifiant du panier AFFICHÉ : c'est lui que visent toutes les mutations.
  // Un ref (et non un state) parce qu'il doit être lu au moment de l'appel, pas
  // au moment où la fonction a été mémorisée — sinon une mutation déclenchée
  // juste après une bascule partirait encore sur l'ancien panier.
  const cartIdRef = useRef(null);
  const jetonRef = useRef(0);

  const nouveauJeton = useCallback(() => {
    jetonRef.current += 1;
    return jetonRef.current;
  }, []);

  /** Appel HTTP + lecture JSON, avec l'échec réseau/proxy rendu lisible. */
  const appelPanier = useCallback(async (url, options) => {
    try {
      return await apiFetch(url, options).then(readApiJson);
    } catch (e) {
      const message = e?.message || "Le portail n'a pas répondu.";
      setErreurPanier(message);
      throw (e instanceof Error ? e : new Error(message));
    }
  }, []);

  /**
   * Applique une réponse comme nouvel état du panier — après vérification.
   * `jeton` absent = application inconditionnelle (mutation isolée).
   */
  const appliquerPanier = useCallback((res, contexte, jeton) => {
    let panier;
    try {
      panier = verifierPanier(res, contexte);
    } catch (e) {
      setErreurPanier(e.message);
      throw e;
    }
    if (jeton === undefined || jeton === jetonRef.current) {
      setCart(panier);
      // Le panier affiché reste la cible des mutations MÊME s'il n'est plus
      // modifiable : le serveur répondra alors 409 avec le motif exact
      // (« déjà envoyé… dupliquez-le »). Retomber en silence sur le panier
      // courant écrirait dans un AUTRE panier que celui affiché.
      cartIdRef.current = panier.id;
      setErreurPanier(null);
    }
    return panier;
  }, []);

  /** Corps JSON d'une mutation, avec le panier visé toujours explicite. */
  const corpsPanier = useCallback((patch = {}) => {
    const identifiant = cartIdRef.current;
    return JSON.stringify(identifiant ? { ...patch, cart_id: identifiant } : patch);
  }, []);

  /** URL d'une mutation sans corps (GET/DELETE), panier visé en paramètre. */
  const urlPanier = useCallback((chemin) => {
    const identifiant = cartIdRef.current;
    if (!identifiant) return chemin;
    const separateur = chemin.includes('?') ? '&' : '?';
    return `${chemin}${separateur}cart_id=${encodeURIComponent(identifiant)}`;
  }, []);

  const loadCartsList = useCallback(async () => {
    try {
      const res = await apiFetch('/api/catalog/carts').then(readApiJson);
      if (res?.ok) setCarts(res.carts || []);
      else console.warn('Liste des paniers refusée par le serveur', res);
    } catch (e) {
      // Liste conservée telle quelle : une liste vidée par un incident réseau
      // ferait croire que les brouillons ont disparu.
      console.warn('Liste des paniers indisponible', e);
    }
  }, []);

  /**
   * Charge un panier précis (`identifiant`) ou le panier courant du serveur
   * (`identifiant` nul). C'est le seul chemin de lecture.
   */
  const chargerPanier = useCallback(async (identifiant) => {
    const cible = identifiant || null;
    const jeton = nouveauJeton();
    setLoading(true);
    try {
      const url = cible
        ? `/api/catalog/cart?cart_id=${encodeURIComponent(cible)}`
        : '/api/catalog/cart';
      const res = await appelPanier(url);
      const panier = appliquerPanier(
        res, cible ? `ouverture du panier ${cible}` : 'chargement du panier', jeton,
      );
      loadCartsList();
      return panier;
    } finally {
      setLoading(false);
    }
  }, [appelPanier, appliquerPanier, loadCartsList, nouveauJeton]);

  /** Recharge le panier AFFICHÉ (et non « le panier courant du serveur »). */
  const reload = useCallback(() => chargerPanier(cartIdRef.current), [chargerPanier]);

  /** Ouvre le panier que le serveur considère comme courant. */
  const chargerPanierCourant = useCallback(() => chargerPanier(null), [chargerPanier]);

  useEffect(() => {
    // Chargement d'amorçage — il ne doit JAMAIS recouvrir un panier déjà
    // demandé explicitement. React exécute les effets des composants ENFANTS
    // avant ceux du provider : l'ouverture d'un `?cart_id=` par la page panier
    // est donc déjà partie quand celui-ci s'exécute. `jetonRef.current > 0`
    // signifie « une lecture est déjà en cours » : inutile d'en lancer une
    // seconde, qui ne pourrait que ramener un autre panier.
    if (jetonRef.current > 0) return;
    // Le message d'échec est porté par `erreurPanier` : inutile de laisser
    // filer un rejet non traité au montage.
    chargerPanierCourant().catch(() => {});
  }, [chargerPanierCourant]);

  /**
   * Ouvre le panier DÉSIGNÉ, quel que soit son schéma d'identifiant.
   *
   * C'est le contrat de `/gestion/panier?cart_id=<identifiant>` : « Dupliquer »
   * un devis, « Convertir » un ticket en panier et le configurateur PC
   * redirigent tous vers cette page en désignant un panier précis.
   */
  const ouvrirPanier = useCallback(async (identifiant) => {
    const brut = String(identifiant || '').trim();
    if (!brut) return null;
    let cible = brut;
    if (estIdentifiantPhase7(brut)) {
      // Panier du configurateur PC : adressé par ObjectId. Le document porte
      // AUSSI un `id` UUID (cf. create_named_draft) — c'est ce dernier que
      // connaissent les routes de mutation.
      const doc = await appelPanier(`/api/catalog/v2/carts/${encodeURIComponent(brut)}`);
      const uuid = doc && typeof doc === 'object' ? doc.id : null;
      if (!uuid) {
        const message = doc?.detail
          ? `Brouillon ${brut} : ${doc.detail}`
          : `Le brouillon ${brut} n'a pas d'identifiant exploitable (créé avant l'unification des schémas).`;
        setErreurPanier(message);
        throw new Error(message);
      }
      cible = uuid;
    }
    const panier = await chargerPanier(cible);
    if ((panier?.status || 'draft') === 'draft') {
      // Un brouillon ouvert devient le panier COURANT côté serveur : la cloche,
      // les autres onglets et l'ajout depuis la fiche produit doivent désigner
      // le même panier que celui affiché ici.
      try {
        await appelPanier(
          `/api/catalog/carts/${encodeURIComponent(panier.id)}/select`, { method: 'POST' },
        );
        loadCartsList();
      } catch (e) {
        console.warn('Panier ouvert mais non promu courant', e);
      }
    }
    return panier;
  }, [appelPanier, chargerPanier, loadCartsList]);

  const createNewCart = useCallback(async (name = '') => {
    const jeton = nouveauJeton();
    const res = await appelPanier('/api/catalog/carts/new', {
      method: 'POST', headers: ENTETES_JSON, body: JSON.stringify({ name }),
    });
    const panier = appliquerPanier(res, "création d'un panier", jeton);
    loadCartsList();
    return panier;
  }, [appelPanier, appliquerPanier, loadCartsList, nouveauJeton]);

  const selectCart = useCallback(async (cartId) => {
    const jeton = nouveauJeton();
    const res = await appelPanier(
      `/api/catalog/carts/${encodeURIComponent(cartId)}/select`, { method: 'POST' },
    );
    const panier = appliquerPanier(res, `bascule sur le panier ${cartId}`, jeton);
    loadCartsList();
    return panier;
  }, [appelPanier, appliquerPanier, loadCartsList, nouveauJeton]);

  const renameCart = useCallback(async (cartId, name) => {
    const res = await appelPanier(`/api/catalog/carts/${encodeURIComponent(cartId)}`, {
      method: 'PATCH', headers: ENTETES_JSON, body: JSON.stringify({ name }),
    });
    if (!res?.ok) {
      const message = res?.error || `Renommage du panier ${cartId} refusé.`;
      setErreurPanier(message);
      throw new Error(message);
    }
    // Le panier affiché porte le nouveau nom sans attendre un rechargement.
    setCart(prev => (prev && prev.id === cartId ? { ...prev, name } : prev));
    loadCartsList();
    return res;
  }, [appelPanier, loadCartsList]);

  const deleteCartDraft = useCallback(async (cartId) => {
    const res = await appelPanier(`/api/catalog/carts/${encodeURIComponent(cartId)}`, {
      method: 'DELETE',
    });
    if (!res?.ok) {
      const message = res?.error || `Suppression du panier ${cartId} refusée.`;
      setErreurPanier(message);
      throw new Error(message);
    }
    if (cartIdRef.current === cartId) {
      // C'était le panier affiché : le backend en a promu un autre (ou en
      // créera un au prochain GET). On suit le serveur.
      cartIdRef.current = null;
      await chargerPanierCourant();
    } else {
      await loadCartsList();
    }
  }, [appelPanier, chargerPanierCourant, loadCartsList]);

  const addItem = useCallback(async (productId, qty = 1) => {
    const jeton = nouveauJeton();
    const res = await appelPanier('/api/catalog/cart/items', {
      method: 'POST', headers: ENTETES_JSON,
      body: corpsPanier({ product_id: productId, qty }),
    });
    return appliquerPanier(res, "ajout d'un article", jeton);
  }, [appelPanier, appliquerPanier, corpsPanier, nouveauJeton]);

  // Smart add: gere automatiquement le cas 0/1/2+ paniers.
  // - 0 panier      -> cree un nouveau panier draft + ajoute (silencieux)
  // - 1 panier      -> ajoute direct dedans (s'assure qu'il est current)
  // - 2+ paniers    -> renvoie {ok:false, needsPick:true, carts:[...]} pour
  //                    que l'UI affiche un modal de choix (cf CartPickerModal).
  // Le caller doit alors appeler addItemTo(cartId, productId).
  const addItemSmart = useCallback(async (productId, qty = 1) => {
    // Refresh la liste des drafts pour decision a jour (un autre onglet a pu
    // creer/supprimer un panier entre-temps).
    let drafts = [];
    try {
      const fresh = await apiFetch('/api/catalog/carts').then(readApiJson);
      drafts = fresh?.ok ? (fresh.carts || []) : (carts || []);
    } catch {
      drafts = carts || [];
    }
    if (drafts.length === 0) {
      const newCart = await createNewCart('');
      await addItem(productId, qty);
      return { ok: true, addedTo: newCart?.id, autoCreated: true };
    }
    if (drafts.length === 1) {
      // Bascule inconditionnelle si ce n'est pas déjà le panier affiché : c'est
      // lui qui devient la cible explicite de l'ajout.
      if (cartIdRef.current !== drafts[0].id) {
        await selectCart(drafts[0].id);
      }
      await addItem(productId, qty);
      return { ok: true, addedTo: drafts[0].id };
    }
    // 2+ -> caller must pick which cart
    return { ok: false, needsPick: true, carts: drafts };
  }, [addItem, createNewCart, selectCart, carts]);

  // Ajoute un produit a un panier specifique (flow smart-add 2+ paniers).
  // Utilise l'endpoint POST /api/catalog/cart/items/to/{cart_id} qui bascule
  // is_current sur le panier cible puis ajoute.
  const addItemTo = useCallback(async (cartId, productId, qty = 1) => {
    const jeton = nouveauJeton();
    const res = await appelPanier(`/api/catalog/cart/items/to/${encodeURIComponent(cartId)}`, {
      method: 'POST', headers: ENTETES_JSON,
      body: JSON.stringify({ product_id: productId, qty }),
    });
    const panier = appliquerPanier(res, `ajout d'un article au panier ${cartId}`, jeton);
    await loadCartsList();
    return panier;
  }, [appelPanier, appliquerPanier, loadCartsList, nouveauJeton]);

  const updateItem = useCallback(async (productId, patch) => {
    const jeton = nouveauJeton();
    const res = await appelPanier(`/api/catalog/cart/items/${encodeURIComponent(productId)}`, {
      method: 'PUT', headers: ENTETES_JSON, body: corpsPanier(patch),
    });
    return appliquerPanier(res, "modification d'une ligne", jeton);
  }, [appelPanier, appliquerPanier, corpsPanier, nouveauJeton]);

  // updateField : patch d'un seul champ sur une ligne (generique pour tous les
  // champs cart-v2 : name_override, description_override, unit, vat_rate,
  // line_discount_pct, internal_note, selected_supplier_id…).
  const updateField = useCallback(async (productId, field, value) => {
    const jeton = nouveauJeton();
    const res = await appelPanier(`/api/catalog/cart/items/${encodeURIComponent(productId)}`, {
      method: 'PUT', headers: ENTETES_JSON, body: corpsPanier({ [field]: value }),
    });
    return appliquerPanier(res, `modification du champ ${field}`, jeton);
  }, [appelPanier, appliquerPanier, corpsPanier, nouveauJeton]);

  const removeItem = useCallback(async (productId) => {
    const jeton = nouveauJeton();
    const res = await appelPanier(
      urlPanier(`/api/catalog/cart/items/${encodeURIComponent(productId)}`), { method: 'DELETE' },
    );
    return appliquerPanier(res, "retrait d'une ligne", jeton);
  }, [appelPanier, appliquerPanier, nouveauJeton, urlPanier]);

  // cleanStaleLines : supprime en bulk toutes les lignes marquees stale_flags
  // (produit archive, prix change, rupture, fournisseur disparu, etc.).
  // Robustesse : on extrait la liste des pids une fois (closure stable),
  // on protege chaque suppression individuellement (un removeItem qui echoue
  // ne bloque pas les autres), et on reload meme en cas d'erreur partielle.
  const cleanStaleLines = useCallback(async () => {
    if (!cart?.items?.length) return { removed: 0, errors: [] };
    const stalePids = cart.items
      .filter(it => Array.isArray(it.stale_flags) && it.stale_flags.length > 0)
      .map(it => it.product_id)
      .filter(Boolean);  // ignore les pids null/undefined
    if (!stalePids.length) return { removed: 0, errors: [] };
    let removed = 0;
    const errors = [];
    for (const pid of stalePids) {
      try {
        await removeItem(pid);
        removed += 1;
      } catch (e) {
        console.error('cleanStaleLines: removeItem failed for', pid, e);
        errors.push({ pid, error: e?.message || String(e) });
      }
    }
    try { await reload(); } catch (e) {
      console.error('cleanStaleLines: reload failed', e);
    }
    return { removed, errors };
  }, [cart, removeItem, reload]);

  // ── Option B : lignes typées (sections / notes / réordonnancement) ────────
  const addLine = useCallback(async (lineType, { title = '', text = '', optional = false, afterLineId = null } = {}) => {
    const jeton = nouveauJeton();
    const res = await appelPanier('/api/catalog/cart/lines', {
      method: 'POST', headers: ENTETES_JSON,
      body: corpsPanier({ line_type: lineType, title, text, optional, after_line_id: afterLineId }),
    });
    return appliquerPanier(res, `ajout d'une ligne ${lineType}`, jeton);
  }, [appelPanier, appliquerPanier, corpsPanier, nouveauJeton]);

  const updateLine = useCallback(async (lineId, patch) => {
    const jeton = nouveauJeton();
    const res = await appelPanier(`/api/catalog/cart/lines/${encodeURIComponent(lineId)}`, {
      method: 'PUT', headers: ENTETES_JSON, body: corpsPanier(patch),
    });
    return appliquerPanier(res, "modification d'une ligne", jeton);
  }, [appelPanier, appliquerPanier, corpsPanier, nouveauJeton]);

  const deleteLine = useCallback(async (lineId) => {
    const jeton = nouveauJeton();
    const res = await appelPanier(
      urlPanier(`/api/catalog/cart/lines/${encodeURIComponent(lineId)}`), { method: 'DELETE' },
    );
    return appliquerPanier(res, "suppression d'une ligne", jeton);
  }, [appelPanier, appliquerPanier, nouveauJeton, urlPanier]);

  // Déplace une ligne d'un cran (dir = -1 monter, +1 descendre) via reorder.
  const moveLine = useCallback(async (lineId, dir) => {
    const ids = (cart?.items || []).map(it => it.line_id).filter(Boolean);
    const idx = ids.indexOf(lineId);
    const target = idx + dir;
    if (idx < 0 || target < 0 || target >= ids.length) return cart;
    const next = ids.slice();
    [next[idx], next[target]] = [next[target], next[idx]];
    const jeton = nouveauJeton();
    const res = await appelPanier('/api/catalog/cart/lines/reorder', {
      method: 'POST', headers: ENTETES_JSON, body: corpsPanier({ line_ids: next }),
    });
    return appliquerPanier(res, 'réordonnancement des lignes', jeton);
  }, [appelPanier, appliquerPanier, cart, corpsPanier, nouveauJeton]);

  // Drag & drop (2026-08-07, parité avec l'ex-éditeur Devis CRM) : insère la
  // ligne srcId AVANT targetId ; targetId null = fin du devis. Même endpoint
  // reorder que les flèches — le serveur renumérote et renvoie le cart.
  // Aligné sur la plomberie multi-paniers (corpsPanier/jeton/appliquerPanier) :
  // sans cart_id explicite, le drop écrirait dans « le panier courant » qui
  // peut différer du panier AFFICHÉ.
  const reorderLine = useCallback(async (srcId, targetId) => {
    const ids = (cart?.items || []).map(it => it.line_id).filter(Boolean);
    if (!ids.includes(srcId) || srcId === targetId) return cart;
    const next = ids.filter(id => id !== srcId);
    const insertAt = targetId ? next.indexOf(targetId) : next.length;
    if (insertAt < 0) return cart;
    next.splice(insertAt, 0, srcId);
    const jeton = nouveauJeton();
    const res = await appelPanier('/api/catalog/cart/lines/reorder', {
      method: 'POST', headers: ENTETES_JSON, body: corpsPanier({ line_ids: next }),
    });
    return appliquerPanier(res, 'réordonnancement des lignes (glisser-déposer)', jeton);
  }, [appelPanier, appliquerPanier, cart, corpsPanier, nouveauJeton]);

  const clearCart = useCallback(async () => {
    const jeton = nouveauJeton();
    const res = await appelPanier('/api/catalog/cart/clear', {
      method: 'POST', headers: ENTETES_JSON, body: corpsPanier(),
    });
    return appliquerPanier(res, 'vidage du panier', jeton);
  }, [appelPanier, appliquerPanier, corpsPanier, nouveauJeton]);

  const updateMeta = useCallback(async (patch) => {
    const jeton = nouveauJeton();
    const res = await appelPanier('/api/catalog/cart/meta', {
      method: 'PUT', headers: ENTETES_JSON, body: corpsPanier(patch),
    });
    return appliquerPanier(res, 'mise à jour des informations du panier', jeton);
  }, [appelPanier, appliquerPanier, corpsPanier, nouveauJeton]);

  /**
   * Bascule l'écran sur le panier courant d'APRÈS un envoi.
   *
   * En envoi réel, le serveur a converti le panier et créé le brouillon suivant :
   * il le désigne dans `current_cart_id`. Sans cette bascule, l'écran reste sur
   * un panier converti que plus aucune modification n'atteindra. En mode test,
   * `current_cart_id` désigne le brouillon inchangé : on le recharge simplement.
   */
  const basculerApresEnvoi = useCallback(async (reponse) => {
    const suivant = reponse?.current_cart_id || null;
    try {
      if (suivant) return await chargerPanier(suivant);
      // Backend plus ancien : le serveur a tout de même promu un brouillon
      // courant, on le suit plutôt que de recharger le panier converti.
      return await chargerPanierCourant();
    } catch (e) {
      console.warn('Bascule sur le panier suivant impossible', e);
      return null;
    }
  }, [chargerPanier, chargerPanierCourant]);

  const sendQuotePro = useCallback(async (body) => {
    const identifiant = cartIdRef.current;
    const res = await appelPanier('/api/catalog/cart/send-quote-pro', {
      method: 'POST', headers: ENTETES_JSON,
      body: JSON.stringify(identifiant ? { ...(body || {}), cart_id: identifiant } : (body || {})),
    });
    if (res?.ok) await basculerApresEnvoi(res);
    return res;
  }, [appelPanier, basculerApresEnvoi]);

  const sendBoutique = useCallback(async (body) => {
    const identifiant = cartIdRef.current;
    const res = await appelPanier('/api/catalog/cart/send-boutique', {
      method: 'POST', headers: ENTETES_JSON,
      body: JSON.stringify(identifiant ? { ...(body || {}), cart_id: identifiant } : (body || {})),
    });
    if (res?.ok) await basculerApresEnvoi(res);
    return res;
  }, [appelPanier, basculerApresEnvoi]);

  const itemsCount = cart?.items_count || 0;
  // Seul un brouillon se modifie. Un panier converti reste consultable : c'est
  // la trace de préparation d'un devis parti chez le client.
  const panierModifiable = !cart || (cart.status || 'draft') === 'draft';

  const effacerErreurPanier = useCallback(() => setErreurPanier(null), []);

  return (
    <CartContext.Provider value={{
      cart, loading, itemsCount,
      cartId: cart?.id || null, panierModifiable,
      erreurPanier, effacerErreurPanier,
      addItem, updateItem, removeItem, clearCart, updateMeta,
      sendQuotePro, sendBoutique, reload,
      // V2 : édition champ par champ + nettoyage bulk stale
      updateField, cleanStaleLines,
      // Option B : sections / notes / réordonnancement
      addLine, updateLine, deleteLine, moveLine, reorderLine,
      // Multi-paniers
      carts, createNewCart, selectCart, renameCart, deleteCartDraft, loadCartsList,
      // Ouverture ciblée (?cart_id=) + retour au panier courant du serveur
      ouvrirPanier, chargerPanierCourant, basculerApresEnvoi,
      // Smart-add (gere 0/1/2+ paniers automatiquement)
      addItemSmart, addItemTo,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart doit être utilisé dans un CartProvider');
  return ctx;
}
